import {
  Avatar,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { DisplayDateTime } from "components/DisplayDate";
import { useRef, useState } from "react";
import { toast } from "react-toastify";

const AdmissionNotesTab = ({ admissionId }) => {
  const queryClient = useQueryClient();
  const [{ token, usersList }] = useKothar();
  const userDetail = JSON.parse(localStorage.getItem("userDetail") || "null");
  const [text, setText] = useState("");

  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(-1);
  const [selectedMentionIds, setSelectedMentionIds] = useState(new Set());
  const [deleteDialogId, setDeleteDialogId] = useState(null);
  const textRef = useRef(null);

  const { data: commentsList = [], isFetching } = useQuery({
    queryKey: ["admission-discussion", admissionId],
    queryFn: () =>
      axios
        .get(`${API_URL}/discussion/${admissionId}?type=student`)
        .then((r) => r.data?.comments ?? []),
    enabled: !!admissionId,
    refetchOnWindowFocus: false,
  });

  const { mutate: postComment, isLoading: isPosting } = useMutation({
    mutationFn: (payload) => axios.post(`${API_URL}/discussion/${admissionId}`, payload),
    onSuccess: () => {
      setText("");
      queryClient.invalidateQueries(["admission-discussion", admissionId]);
    },
    onError: () => toast.error("Failed to add note"),
  });

  const deleteComment = async () => {
    try {
      await axios.delete(`${API_URL}/discussion/comment/${deleteDialogId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      queryClient.invalidateQueries(["admission-discussion", admissionId]);
    } catch {
      toast.error("Failed to delete note");
    } finally {
      setDeleteDialogId(null);
    }
  };

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);

    const cursorPos = e.target.selectionStart;
    const textUpToCursor = val.slice(0, cursorPos);
    const atMatch = textUpToCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionStart(cursorPos - atMatch[0].length);
      setMentionAnchor(textRef.current);
    } else {
      setMentionAnchor(null);
      setMentionQuery("");
      setMentionStart(-1);
    }
  };

  const selectMention = (user) => {
    const cursorPos = textRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, mentionStart);
    const after = text.slice(cursorPos);
    setText(before + "@" + user.name + " " + after);
    setSelectedMentionIds((prev) => new Set([...prev, user.id]));
    setMentionAnchor(null);
    setMentionQuery("");
    setMentionStart(-1);
  };

  const mentionOptions = (usersList || [])
    .filter((u) => u.name?.toLowerCase().startsWith(mentionQuery.toLowerCase()))
    .slice(0, 8);

  if (!admissionId) {
    return (
      <Card className="p-6 text-center border-app-border">
        <Typography variant="body2" color="text.secondary">
          Save this admission first to add and view notes.
        </Typography>
      </Card>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    postComment({ text, type: "student", mentionedUserIds: [...selectedMentionIds] });
    setSelectedMentionIds(new Set());
  };

  return (
    <div className="space-y-4">
      <Typography variant="body2" color="text.secondary" className="max-w-3xl">
        Internal notes and discussion for this admission. Type @ to mention a team member.
      </Typography>

      <form onSubmit={handleSubmit} className="space-y-2">
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          placeholder="Write a note... Type @ to mention someone"
          value={text}
          onChange={handleTextChange}
          inputRef={textRef}
        />
        <Popper
          open={Boolean(mentionAnchor) && mentionOptions.length > 0}
          anchorEl={mentionAnchor}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
        >
          <Paper elevation={3}>
            {mentionOptions.map((u) => (
              <MenuItem
                key={u.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectMention(u);
                }}
              >
                <Avatar sx={{ width: 24, height: 24, fontSize: 12, mr: 1 }}>{u.name?.[0]}</Avatar>
                {u.name}
              </MenuItem>
            ))}
          </Paper>
        </Popper>
        <Button type="submit" variant="contained" size="small" disabled={isPosting || !text.trim()}>
          {isPosting ? "Posting..." : "Add note"}
        </Button>
      </form>

      {isFetching ? (
        <div className="flex justify-center py-8">
          <CircularProgress size={28} />
        </div>
      ) : commentsList.length === 0 ? (
        <Card className="p-6 text-center border-app-border">
          <Typography variant="body2" color="text.secondary">
            No notes yet.
          </Typography>
        </Card>
      ) : (
        <ul className="space-y-3">
          {commentsList.map((comment) => {
            const author =
              comment?.createdBy ||
              usersList?.find((u) => u.id === comment?.commentedById)?.name ||
              "User";
            return (
              <li key={comment.id}>
                <Card variant="outlined" className="p-3 border-app-border">
                  <div className="flex items-start gap-2">
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                      {author.charAt(0).toUpperCase()}
                    </Avatar>
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <Typography variant="subtitle2">{author}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          <DisplayDateTime value={comment.createdDate} fallback="" />
                        </Typography>
                      </div>
                      <Typography variant="body2" className="mt-1 whitespace-pre-wrap">
                        {comment.text}
                      </Typography>
                    </div>
                    {userDetail?.id === comment?.commentedById && (
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialogId(comment.id)}
                        aria-label="Delete note"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
      <Dialog open={!!deleteDialogId} onClose={() => setDeleteDialogId(null)}>
        <DialogTitle>Delete note</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This note will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogId(null)}>Cancel</Button>
          <Button onClick={deleteComment} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdmissionNotesTab;
